// src/contexts/financeiro/RecebimentoContext.js
import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAlert } from '../geral/AlertContext';
import { useMessage } from '../geral/MessageContext';
import {
    adicionarRecebimento,
    alterarRecebimento,
    buscarRecebimentos as buscarRecebimentosService,
    buscarTodosRecebimentos,
    excluirRecebimento as excluirRecebimentoService,
    excluirParcelaRecebimento,
} from '../../services/financeiro/RecebimentoService';

const RecebimentoContext = createContext();

export const useRecebimento = () => useContext(RecebimentoContext);

export const RecebimentoProvider = ({ children }) => {
    const [recebimentos, setRecebimentos] = useState([]);
    const { showAlert } = useAlert();
    const { showMessage } = useMessage();

    const fetchRecebimentos = useCallback(async () => {
        try {
            const result = await buscarTodosRecebimentos();
            setRecebimentos(result);                        
        } catch (error) {
            console.error("Erro ao buscar recebimentos:", error);
            showMessage(`Erro ao buscar recebimentos! ${error?.message || error}`, 'danger');
        }
    }, [showMessage]); // Dependências de fetchRecebimentos

    const buscarRecebimentos = useCallback(async (filtros) => {
        const result = await buscarRecebimentosService(filtros);
        setRecebimentos(result || []);
        return result || [];
    }, []);

    const addRecebimento = async (recebimento) => {
        try {
            const result = await adicionarRecebimento(recebimento);
            showAlert('Recebimento adicionado com sucesso!');
            return result;
        } catch (error) {
            console.error("Erro ao adicionar recebimento:", error);
            showMessage(`Erro ao adicionar recebimento! ${error?.message || error}`, 'danger');
            throw error;
        }
    };

    const updateRecebimento = async (recebimento) => {
        try {
            const result = await alterarRecebimento(recebimento);
            setRecebimentos((prev) =>
                prev.map((r) => (r.id === result.id ? result : r))
            );
            showAlert('Recebimento alterado com sucesso!');
        } catch (error) {
            console.error("Erro ao atualizar recebimento:", error);
            showAlert(`Erro ao alterar recebimento! ${error?.message || error}`, 'danger');
        }
    };

    const excluirRecebimento = async (id, justificativa) => {
        try {
            await excluirRecebimentoService(id, justificativa);
            setRecebimentos((prev) => prev.filter((recebimento) => recebimento.id !== id));
            showAlert('Recebimento excluído com sucesso!');
        } catch (error) {
            console.error("Erro ao excluir recebimento:", error);
            showMessage(`Erro ao excluir recebimento! ${error?.message || error}`, 'danger');
            throw error;
        }
    };

    const deleteRecebimentoParcela = async (id, justificativa) => {
        try {
            await excluirParcelaRecebimento(id, justificativa);
            setRecebimentos((prev) => prev
                .map((recebimento) => ({
                    ...recebimento,
                    recebimentoParcelas: (recebimento.recebimentoParcelas || [])
                        .filter((parcela) => parcela.id !== id),
                }))
                .filter((recebimento) => recebimento.recebimentoParcelas.length > 0));
            showAlert('Parcela excluída com sucesso!');
        } catch (error) {
            console.error("Erro ao excluir parcela do recebimento:", error);
            showAlert(`Erro ao excluir parcela! ${error?.message || error}`, 'danger');
            throw error;
        }
    };

    return (
        <RecebimentoContext.Provider value={{ recebimentos, fetchRecebimentos, buscarRecebimentos, addRecebimento, updateRecebimento, excluirRecebimento, deleteRecebimentoParcela, setRecebimentos }}>
            {children}
        </RecebimentoContext.Provider>
    );
};
